import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Popover } from "@material-ui/core";
import { NotificationsNone } from "@material-ui/icons";
import api from "../api";
import "./NotificationBell.scss";

const POLL_MS = 60 * 1000;

const timeAgo = (date) => {
  const s = Math.max(0, (Date.now() - new Date(date).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

/**
 * Header bell: follows, likes, comments and shared-list activity (the same
 * Notification rows the iOS app gets as pushes and the extension polls).
 * Polls the unread count while the tab is visible; opening the list marks
 * everything read.
 */
const NotificationBell = ({ className = "", label }) => {
  const history = useHistory();
  const location = useLocation();
  const [anchor, setAnchor] = useState(null);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const mounted = useRef(true);

  const load = useCallback(() => {
    api
      .getNotifications(30)
      .then((res) => {
        if (!mounted.current) return;
        setItems(res.notifications || []);
        setUnread(res.unread || 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    load();
  }, [load, location.pathname]);

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "visible") load();
    };
    const timer = setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [load]);

  const open = (e) => {
    setAnchor(e.currentTarget);
    if (unread > 0) {
      setUnread(0);
      api.markNotificationsRead().catch(() => {});
    }
  };

  const close = () => {
    setAnchor(null);
    // Unread dots stay on screen until the list is closed.
    setItems((list) => list.map((n) => ({ ...n, read: true })));
  };

  const go = (n) => {
    close();
    if (!n.link) return;
    try {
      const url = new URL(n.link, window.location.origin);
      if (url.origin === window.location.origin) return history.push(url.pathname + url.search);
      // Links are built with the server's DOMAIN; same app, different host
      // (www vs bare, dev) still stays in the app.
      if (/ollacart/.test(url.hostname)) return history.push(url.pathname + url.search);
      window.open(url.href, "_blank", "noopener");
    } catch (e) {}
  };

  return (
    <>
      <button type="button" className={`notif-bell ${className}`.trim()} onClick={open} aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`}>
        <span className="notif-bell__icon">
          <NotificationsNone />
          {unread > 0 && <span className="notif-bell__badge">{unread > 99 ? "99+" : unread}</span>}
        </span>
        {label && <span className="notif-bell__label">{label}</span>}
      </button>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: "notif-panel" }}
      >
        <div className="notif-panel__title">Notifications</div>
        {items.length === 0 ? (
          <div className="notif-panel__empty">
            When friends follow you, like or comment on your items, it shows up here.
          </div>
        ) : (
          <ul className="notif-panel__list">
            {items.map((n) => (
              <li key={n._id}>
                <button type="button" className={`notif-item${n.read ? "" : " is-unread"}`} onClick={() => go(n)}>
                  {n.actor?.photo ? (
                    <img className="notif-item__avatar" src={n.actor.photo} alt="" />
                  ) : (
                    <span className="notif-item__avatar notif-item__avatar--initial">
                      {(n.actor?.username || "?").charAt(0).toUpperCase()}
                    </span>
                  )}
                  <span className="notif-item__text">
                    <span className="notif-item__message">{n.message}</span>
                    <span className="notif-item__time">{timeAgo(n.createdAt)}</span>
                  </span>
                  {n.image && <img className="notif-item__thumb" src={n.image} alt="" />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Popover>
    </>
  );
};

export default NotificationBell;
